import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, afterNextRender, computed, inject, input, signal, viewChild } from '@angular/core';

// 'default' cubre la tarjeta normal (.box-card); 'tc' cubre la tarjeta de crédito (.box-tc,
// clase adicional add-benefit-tc); 'new' cubre la tarjeta "nueva" (.new-box), que usa un tamaño
// de corona y texto distinto a los otros dos.
export type UiBenefitBadgeLayout = 'default' | 'tc' | 'new' | 'summary';

@Component({
  selector: 'app-ui-benefit-badge',
  template: `
    <div
      class="ui-benefit-badge"
      [class.ui-benefit-badge--tc]="layout() === 'tc'"
      [class.ui-benefit-badge--new]="layout() === 'new'"
      [class.ui-benefit-badge--summary]="layout() === 'summary'"
    >
      @if (showCrown()) {
      <img title="crown" class="crown" alt="corona-beneficio" src="assets/icons/Icono_corona_adicional.svg" />
      }
      <div class="box-benefit">
        <div class="descBenefit-window" [class.descBenefit-window--marquee]="isTextLong()" #benefitWindow>
          <p class="descBenefit"><span #benefitText>
            @for (seg of segments(); track $index) 
              {<span [class.descBenefit__bold]="seg.bold">{{ seg.text }}</span>}</span>
          </p>
        </div>
      </div>
    </div>
  `,
  styleUrl: './ui-benefit-badge.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiBenefitBadgeAtomComponent {
  readonly layout = input<UiBenefitBadgeLayout>('default');
  readonly showCrown = input(true);
  readonly text = input('');

  // Desde el primer separador el texto va en negrilla; el '$' se incluye en la parte resaltada
  // porque forma parte del monto (p.ej. "Cobro x cada 100 mil: $10.000").
  protected readonly segments = computed<Array<{ text: string; bold: boolean }>>(() => {
    const value = this.text();
    const match = /[,:;.$!¡]/.exec(value);
    if (!value || !match) {
      return value ? [{ text: value, bold: false }] : [];
    }

    const boldStart = match[0] === '$' ? match.index : match.index + 1;
    return [
      { text: value.slice(0, boldStart), bold: false },
      { text: value.slice(boldStart), bold: true },
    ].filter((seg) => seg.text.length > 0);
  });

  private readonly benefitWindow = viewChild<ElementRef<HTMLElement>>('benefitWindow');
  private readonly benefitText = viewChild<ElementRef<HTMLElement>>('benefitText');

  protected readonly isTextLong = signal(false);

  constructor() {
    const destroyRef = inject(DestroyRef);

    // Mide el texto proyectado tras el primer render para activar el carrusel solo si desborda.
    afterNextRender(() => {
      const windowEl = this.benefitWindow()?.nativeElement;
      const textEl = this.benefitText()?.nativeElement;
      if (!windowEl || !textEl) return;

      const checkOverflow = () => this.isTextLong.set(textEl.scrollWidth > windowEl.clientWidth);
      checkOverflow();

      const observer = new ResizeObserver(checkOverflow);
      observer.observe(windowEl);
      observer.observe(textEl);
      destroyRef.onDestroy(() => observer.disconnect());
    });
  }
}
